import { Tooltip, Box } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import JxLineEdit from './JxLineEdit';
import { tooltipSx } from '../../components/shared/jernyTooltipStyling';

const COPY_RESET_MS = 1500;

/**
 * JxCopyEdit — JxLineEdit with a built-in copy-to-clipboard suffix (icon + tooltip + "Copied!"
 * flip). Extracted 20260703 from three near-identical copy-button blocks that had grown in
 * CsCodeGenForm.jsx (Defaults RK, RowKey) and CsCodeGenProjectSection.jsx (Target folder) — same
 * useState(copied) + handleCopy + Tooltip/Box/ContentCopyIcon/CheckIcon JSX repeated three times.
 * Has real JS behaviour (state, click handler, timeout) so this is a proper JxControls component,
 * not a CSS-only style export like jxScrollbarThin/tooltipSx (see Development.Patterns
 * rk=684aad957f414a8490b102a2df7981ae — "wanneer niet: zodra er JS-gedrag bijkomt").
 * No implicit `disabled` default — pass it explicitly like any other JxLineEdit usage (all current
 * call sites are readonly display fields, so they pass disabled={true}).
 * @param {{
 *   value: string,               // displayed text
 *   copyValue?: string,          // what actually gets copied — defaults to `value`. Only needed
 *                                 // when the display shows a fallback/placeholder that isn't itself
 *                                 // a valid value to copy (e.g. "MOCK_DEFAULTS (BE cache leeg)").
 *   label: string,               // used for the default tooltip: "Copy {label} to Clipboard"
 *   hint?: string,                // overrides the default tooltip text entirely
 *   disabled?: boolean,
 *   onChange?: (value: string) => void, // only relevant if disabled={false}
 *   onClear?: () => void,         // optional — shows a clear icon left of the copy icon
 * }} props
 */
export default class JxCopyEdit extends JxLineEdit {
    constructor(props) {
        super(props);
        this.state = { copied: false };
    }

    getPropsToOmit() {
        return ['copyValue', 'label', 'hint', 'onClear'];
    }

    _handleCopy = () => {
        const toCopy = this.props.copyValue ?? this.props.value;
        if (!toCopy) return;
        navigator.clipboard.writeText(toCopy).then(() => {
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), COPY_RESET_MS);
        });
    };

    getSuffix() {
        const { copied } = this.state;
        const tooltipTitle = copied ? 'Copied!' : (this.props.hint ?? `Copy ${this.props.label} to Clipboard`);
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {this.props.onClear && (
                    <Tooltip title={`Clear ${this.props.label}`} componentsProps={tooltipSx}>
                        <Box sx={{ cursor: 'pointer', display: 'flex', color: 'inherit', mr: '4px' }} onClick={this.props.onClear}>
                            <ClearIcon sx={{ fontSize: 12 }} />
                        </Box>
                    </Tooltip>
                )}
                <Tooltip title={tooltipTitle} componentsProps={tooltipSx}>
                    <Box sx={{ cursor: 'pointer', display: 'flex', color: 'inherit', mr: '4px' }} onClick={this._handleCopy}>
                        {copied ? <CheckIcon sx={{ fontSize: 12 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
                    </Box>
                </Tooltip>
            </Box>
        );
    }
}
